/**
 * The economy seam: the bench's job queue and the three market counters.
 *
 * Reads go through `apiTry` (a server without these routes yet → `null` → an honest empty
 * state, never a stack trace); writes throw, because the player pressed a button and is owed
 * a reason. Every shape is normalised here rather than in the components, so a field the
 * server renames cannot reach a render function as `undefined`.
 */

import { ItemLocation, type FactionId, type ItemStack } from '@stillsun/shared';
import { api, apiTry } from './api.js';

const num = (v: unknown, fallback = 0): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback;
const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const rec = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' ? (v as Record<string, unknown>) : {};

// ------------------------------------------------------------------ the bench

export interface CraftJob {
  id: number;
  recipeId: string;
  name: string;
  fee: number;
  /** Unix seconds, on the server's clock. */
  startedAt: number;
  readyAt: number;
  durationSec: number;
  remainingSec: number;
  ready: boolean;
  output: { itemId: string; qty: number };
}

export interface CraftJobsResponse {
  jobs: CraftJob[];
  queueMax: number;
  workbenchLevel: number;
  wick: number;
  /** How many of each item id the player owns, anywhere — what `start` validates against. */
  materials: Record<string, number>;
  /** The server's `now` at the moment of the read; the bench clock is anchored to this. */
  nowSec: number;
}

function jobOf(raw: unknown): CraftJob | null {
  const j = rec(raw);
  const id = num(j.id, -1);
  if (id < 0) return null;
  const out = rec(j.output);
  return {
    id,
    recipeId: str(j.recipeId),
    name: str(j.name, str(j.recipeId)),
    fee: num(j.fee),
    startedAt: num(j.startedAt),
    readyAt: num(j.readyAt),
    durationSec: Math.max(1, num(j.durationSec, 1)),
    remainingSec: Math.max(0, num(j.remainingSec)),
    ready: j.ready === true,
    output: { itemId: str(out.itemId), qty: Math.max(1, num(out.qty, 1)) },
  };
}

function jobsOf(raw: unknown): CraftJob[] {
  const list = Array.isArray(raw) ? raw : [];
  const out: CraftJob[] = [];
  for (const item of list) {
    const job = jobOf(item);
    if (job) out.push(job);
  }
  return out;
}

export async function fetchCraftJobs(token: string): Promise<CraftJobsResponse | null> {
  const raw = await apiTry<unknown>('/api/craft/jobs', { token });
  if (raw === null) return null;
  const r = rec(raw);
  const materials: Record<string, number> = {};
  for (const [k, v] of Object.entries(rec(r.materials))) {
    if (typeof v === 'number' && Number.isFinite(v)) materials[k] = v;
  }
  return {
    jobs: jobsOf(r.jobs),
    queueMax: Math.max(1, num(r.queueMax, 3)),
    workbenchLevel: Math.max(1, num(r.workbenchLevel, 1)),
    wick: num(r.wick),
    materials,
    nowSec: num(r.nowSec, Math.floor(Date.now() / 1000)),
  };
}

/**
 * The bench is drawn in one component (`WorkbenchScene`) and started from two (the scene's own
 * picker and the recipe list below it), so a write announces the new queue rather than making
 * either of them own the other. One module-level set of listeners; no store, no context.
 */
type BenchListener = (jobs: CraftJob[]) => void;
const benchListeners = new Set<BenchListener>();

export function onBenchChange(fn: BenchListener): () => void {
  benchListeners.add(fn);
  return () => {
    benchListeners.delete(fn);
  };
}

/** Every write returns the whole queue, so one round trip both acts and refreshes. */
async function jobWrite(
  path: string,
  token: string,
  body: Record<string, unknown>,
): Promise<CraftJob[]> {
  const raw = await api<unknown>(path, { body, token });
  const jobs = jobsOf(rec(raw).jobs);
  for (const fn of [...benchListeners]) {
    try {
      fn(jobs);
    } catch (err) {
      console.error('[stillsun] bench listener threw', err);
    }
  }
  return jobs;
}

export const startCraftJob = (token: string, recipeId: string): Promise<CraftJob[]> =>
  jobWrite('/api/craft/jobs', token, { recipeId });

export const collectCraftJob = (token: string, jobId: number): Promise<CraftJob[]> =>
  jobWrite(`/api/craft/jobs/${String(jobId)}/collect`, token, {});

export const cancelCraftJob = (token: string, jobId: number): Promise<CraftJob[]> =>
  jobWrite(`/api/craft/jobs/${String(jobId)}/cancel`, token, {});

// ----------------------------------------------------------------- the market

export interface MarketStockLine {
  itemId: string;
  /** The sign price, before standing. */
  list: number;
  /** What this player actually pays. */
  price: number;
  rotating: boolean;
}

export interface MarketCounter {
  id: FactionId;
  name: string;
  sign: string;
  blurb: string;
  rep: number;
  tier: number;
  standing: string;
  /** 0..0.15. */
  discount: number;
  insurance: boolean;
  stock: MarketStockLine[];
}

export type MarketStashRow = ItemStack & { sell: number; insurePrice: number };

export interface MarketView {
  dayIndex: number;
  wick: number;
  counters: MarketCounter[];
  stash: MarketStashRow[];
}

const COUNTER_IDS: readonly FactionId[] = ['compact', 'hymnal', 'ferry'];
const isFactionId = (s: string): s is FactionId => (COUNTER_IDS as readonly string[]).includes(s);

function counterOf(raw: unknown): MarketCounter | null {
  const c = rec(raw);
  const id = str(c.id);
  if (!isFactionId(id)) return null;
  const stock: MarketStockLine[] = [];
  for (const item of Array.isArray(c.stock) ? c.stock : []) {
    const line = rec(item);
    const itemId = str(line.itemId);
    if (itemId.length === 0) continue;
    const list = num(line.list);
    stock.push({
      itemId,
      list,
      price: num(line.price, list),
      rotating: line.rotating === true,
    });
  }
  return {
    id,
    name: str(c.name, id),
    sign: str(c.sign),
    blurb: str(c.blurb),
    rep: num(c.rep),
    tier: num(c.tier),
    standing: str(c.standing, 'Stranger'),
    discount: num(c.discount),
    insurance: c.insurance === true,
    stock,
  };
}

function stashOf(raw: unknown): MarketStashRow[] {
  const out: MarketStashRow[] = [];
  for (const item of Array.isArray(raw) ? raw : []) {
    const s = rec(item);
    const uid = num(s.uid, -1);
    const itemId = str(s.itemId);
    if (uid < 0 || itemId.length === 0) continue;
    out.push({
      uid,
      itemId,
      qty: Math.max(1, num(s.qty, 1)),
      location: ItemLocation.Stash,
      slot: num(s.slot),
      insured: s.insured === true,
      // Absent means pristine — `durabilityOf` says so, and every row written before the column
      // existed reads that way. Never defaulted to 0 here: that would break every ledger row.
      ...(typeof s.durability === 'number' ? { durability: s.durability } : {}),
      sell: num(s.sell),
      insurePrice: num(s.insurePrice),
    });
  }
  return out;
}

export async function fetchMarket(token: string): Promise<MarketView | null> {
  const raw = await apiTry<unknown>('/api/vendor', { token });
  if (raw === null) return null;
  const r = rec(raw);
  const counters: MarketCounter[] = [];
  for (const item of Array.isArray(r.counters) ? r.counters : []) {
    const counter = counterOf(item);
    if (counter) counters.push(counter);
  }
  return {
    dayIndex: num(r.dayIndex),
    wick: num(r.wick),
    counters,
    stash: stashOf(r.stash),
  };
}

export interface SellReceipt {
  paid: number;
  rep: number;
  itemId: string;
  qty: number;
}

export async function sellItem(token: string, uid: number, qty: number): Promise<SellReceipt> {
  const raw = rec(await api<unknown>('/api/vendor/sell', { body: { uid, qty }, token }));
  return {
    paid: num(raw.paid),
    rep: num(raw.rep),
    itemId: str(raw.itemId),
    qty: num(raw.qty, qty),
  };
}

export interface BuyReceipt {
  spent: number;
  itemId: string;
  qty: number;
}

export async function buyItem(
  token: string,
  counter: FactionId,
  itemId: string,
  qty = 1,
): Promise<BuyReceipt> {
  const raw = rec(await api<unknown>('/api/vendor/buy', { body: { counter, itemId, qty }, token }));
  return { spent: num(raw.spent), itemId: str(raw.itemId, itemId), qty: num(raw.qty, qty) };
}

export async function insureItem(
  token: string,
  uid: number,
): Promise<{ premium: number; itemId: string }> {
  const raw = rec(await api<unknown>('/api/vendor/insure', { body: { uid }, token }));
  return { premium: num(raw.premium), itemId: str(raw.itemId) };
}

// ----------------------------------------------------------------- the repair bench
//
// Phase 2c. `repairCost` / `repairAllCost` in the shared contract are what the panel quotes and
// what the server charges — one set of numbers, so a player is never surprised at the counter.
// The route is `POST /api/vendor/repair` with `{ uid }` for one piece or `{ all: true }` for the
// lot; both answer `{ spent }`. A 4xx (or a server that has not shipped the route yet) throws
// through `api`, and the Vendor panel says so rather than pretending the piece was mended.

export interface RepairReceipt {
  /** Wick actually taken. 0 is a legitimate answer: nothing was damaged. */
  spent: number;
}

const repairWrite = async (
  token: string,
  body: Record<string, unknown>,
): Promise<RepairReceipt> => {
  const raw = rec(await api<unknown>('/api/vendor/repair', { body, token }));
  return { spent: num(raw.spent) };
};

/** Mend one stack back to `MAX_DURABILITY`. */
export const repairItem = (token: string, uid: number): Promise<RepairReceipt> =>
  repairWrite(token, { uid });

/** Mend everything the player owns that is damaged, in one charge. */
export const repairAll = (token: string): Promise<RepairReceipt> =>
  repairWrite(token, { all: true });
